"use client"
import { forwardRef, useImperativeHandle, useRef, useState } from "react"
import type { QueueItem } from "@/lib/review/queue"
import type { Glossary } from "@/lib/review/glossary"
import { type LocateState } from "./locate-state"
import type { ReviewPlace } from "./jump-to-string"
import { PlacesPanel } from "./places-panel"
import { LargeStringEditor } from "./large-string-editor"
import { useReviewItemActions, statusLabel } from "./use-review-item-actions"

/** Imperative surface the worklist's keyboard handler drives. */
export type ReviewPanelHandle = {
  approve: () => void
  focusEdit: () => void
}

const LOCATE_NOTICE: Record<Exclude<LocateState, "page">, string> = {
  chrome: "Shared UI — shown across the site, not tied to one page.",
  template: "Contains placeholders ({…}) — can't highlight in context.",
}

export const ReviewPanel = forwardRef<
  ReviewPanelHandle,
  {
    item: QueueItem
    onDone: () => void
    idToken: string
    glossary: Glossary
    locate: LocateState
    /** Pages this string renders on (for off-page strings); empty otherwise. */
    places: ReviewPlace[]
    /** Soft-nav the cockpit to a page href and re-highlight the string. */
    onPreview: (href: string) => void
    /** Href to view this string on its page when the reviewer isn't already
     *  there (page strings only); null when on-page or not a page string. */
    pageHref: string | null
  }
>(function ReviewPanel(
  { item, onDone, idToken, glossary, locate, places, onPreview, pageHref },
  ref
) {
  const [fullOpen, setFullOpen] = useState(false)
  const targetRef = useRef<HTMLTextAreaElement>(null)
  const {
    value,
    setValue,
    busy,
    edited,
    large,
    issues,
    blocked,
    approveAsIs: approveAsIsAction,
    editAndApprove: editAndApproveAction,
  } = useReviewItemActions({ item, onDone, idToken, glossary })

  const errorCount = issues.filter((i) => i.severity === "error").length

  function approveAsIs() {
    setFullOpen(false)
    approveAsIsAction()
  }
  function editAndApprove() {
    setFullOpen(false)
    editAndApproveAction()
  }

  useImperativeHandle(
    ref,
    () => ({
      approve: () => {
        if (busy) return
        if (edited) editAndApprove()
        else approveAsIs()
      },
      focusEdit: () => {
        if (large) setFullOpen(true)
        else targetRef.current?.focus()
      },
    }),
    [busy, edited, large] // eslint-disable-line react-hooks/exhaustive-deps
  )

  return (
    <div className="ahead-review-panel">
      <div className="ahead-review-meta">
        {item.key} · {item.tier} · {statusLabel(item.status)}
      </div>
      {pageHref ? (
        // A page string the reviewer isn't currently on — offer an explicit jump
        // (the route is prefetched, so it's an instant soft swap). A real <a> so
        // ⌘/Ctrl/middle-click opens the page in its own tab; a plain click soft-
        // navs in place and re-highlights via onPreview.
        <a
          className="ahead-review-viewpage"
          href={pageHref}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
            e.preventDefault()
            onPreview(pageHref)
          }}
        >
          View on page →
        </a>
      ) : null}
      {locate !== "page" ? (
        <>
          <p className="ahead-review-banner" role="note">
            {LOCATE_NOTICE[locate]}
          </p>
          <PlacesPanel places={places} onPreview={onPreview} />
        </>
      ) : null}
      <label>
        EN source
        <textarea readOnly value={item.enValue} aria-label="EN source" />
      </label>
      {large ? (
        <div className="ahead-review-large">
          <p className="ahead-review-large-preview">
            {value.slice(0, 200)}
            {value.length > 200 ? "…" : ""}
          </p>
          <button
            className="ahead-review-large-open"
            onClick={() => setFullOpen(true)}
          >
            Edit in full editor <kbd className="ahead-review-kbd">E</kbd>
          </button>
        </div>
      ) : (
        <label>
          {item.locale}
          <textarea
            ref={targetRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            aria-label={item.locale}
          />
        </label>
      )}
      {issues.length > 0 ? (
        <>
          {blocked ? (
            <p className="ahead-review-issues-blocker" role="alert">
              {errorCount === 1
                ? "1 error blocks approval"
                : `${errorCount} errors block approval`}
            </p>
          ) : null}
          <ul className="ahead-review-issues" role="note">
            {issues.map((issue) => (
              <li
                key={`${issue.code}:${issue.severity}`}
                data-severity={issue.severity}
              >
                {issue.severity === "error" ? "Error" : "Warning"} —{" "}
                {issue.message}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <div className="ahead-review-actions">
        <button disabled={busy || edited || blocked} onClick={approveAsIs}>
          Approve as-is <kbd className="ahead-review-kbd">A</kbd>
        </button>
        <button disabled={busy || !edited || blocked} onClick={editAndApprove}>
          Edit &amp; approve <kbd className="ahead-review-kbd">⌘↵</kbd>
        </button>
        <button disabled={busy} onClick={onDone}>
          Skip <kbd className="ahead-review-kbd">S</kbd>
        </button>
      </div>
      {blocked ? (
        <p className="ahead-review-blocked-hint" role="note">
          Fix the error above to approve.
        </p>
      ) : null}
      {fullOpen ? (
        <LargeStringEditor
          keyName={item.key}
          tier={item.tier}
          enValue={item.enValue}
          value={value}
          dirty={edited}
          busy={busy}
          blocked={blocked}
          onChange={setValue}
          onApprove={approveAsIs}
          onEditApprove={editAndApprove}
          onClose={() => setFullOpen(false)}
        />
      ) : null}
    </div>
  )
})
